import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DividerMotif from "../../components/Brand/DividerMotif";
import Footer from "../../components/Layout/Footer";
import { PublicHeader } from "../../components/Layout/PublicHeader";
import { listJobs } from "../../lib/jobsApi";
import type { PublicJobSummary } from "../../lib/jobsApi";
import { ApiError } from "../../lib/api";
import { formatRelativeDate } from "../../lib/formatRelativeDate";
import { useDocumentMeta } from "../../lib/useDocumentMeta";

/*
 * Velaigal /jobs — the public job board.
 *
 * Fully public: no ProtectedRoute, no app Layout/Sidebar. Renders the same
 * header/footer shell as the other public pages (Features, Status) so it
 * reads as part of the marketing site, not a logged-in dashboard. Signed-in
 * job seekers reach this page via the Sidebar "Jobs" link, which takes them
 * OUT of the app shell on purpose — the job board is the same page for
 * everyone, logged in or not.
 */

const JobCard: React.FC<{ job: PublicJobSummary }> = ({ job }) => (
  <Link
    to={`/jobs/${job.id}`}
    className="card block no-underline text-ink"
    style={{ textDecoration: "none" }}
  >
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
      <div className="min-w-0">
        <span className="label-editorial" style={{ color: "var(--color-saffron)" }}>
          {job.roleFamily}
        </span>
        <h2 className="font-display text-xl font-medium text-ink leading-snug mt-2 velaigal-headline-hover">
          {job.title}
        </h2>
        <p className="mt-1 text-sm text-ink-secondary">{job.companyName}</p>
      </div>
      <div className="text-left md:text-right shrink-0">
        {job.salaryDisplay && (
          <p className="text-sm font-medium text-ink">{job.salaryDisplay}</p>
        )}
        <p className="mt-1 text-xs text-ink-tertiary">{formatRelativeDate(job.publishedAt)}</p>
      </div>
    </div>
  </Link>
);

const LoadingBlock: React.FC = () => (
  <div className="py-16 text-center">
    <span
      aria-hidden="true"
      className="velaigal-loader-rule mx-auto w-12 block"
    />
    <p className="mt-6 text-sm text-ink-tertiary">Loading open roles.</p>
  </div>
);

const EmptyBlock: React.FC = () => (
  <div className="py-16 text-center">
    <span className="label-editorial">Nothing open right now</span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">
      No roles are published yet.
    </h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">
      Check back soon — new roles land here the moment a recruiter publishes them.
    </p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: "var(--color-error)" }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">
      Couldn&apos;t load open roles.
    </h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const JobsList: React.FC = () => {
  const [jobs, setJobs] = useState<PublicJobSummary[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDocumentMeta(
    "Browse jobs · Velaigal",
    "Open roles built around real tasks instead of trick questions. Apply directly — no assessments, no gatekeeping quizzes."
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listJobs();
      setJobs(res.jobs);
      setNext(res.next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load jobs. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const loadMore = async () => {
    if (!next || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await listJobs(next);
      setJobs((prev) => [...prev, ...res.jobs]);
      setNext(res.next);
    } catch {
      // Keep the existing list visible; the button stays put so the user can retry.
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      <section className="relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-8 pt-20 pb-16">
          <span className="label-editorial block">Open roles</span>
          <h1
            className="font-display text-4xl md:text-5xl font-medium text-ink leading-tight tracking-tight mt-6 max-w-3xl"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Jobs built around what you can{" "}
            <span className="text-saffron">actually do.</span>
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-6 text-base lg:text-lg text-ink-secondary max-w-2xl leading-relaxed">
            Every role here comes with real tasks instead of trick questions.
            Apply directly — no assessments, no gatekeeping quizzes.
          </p>
        </div>
      </section>

      <DividerMotif className="py-2" />

      <section>
        <div className="max-w-5xl mx-auto px-8 py-16">
          {loading ? (
            <LoadingBlock />
          ) : error ? (
            <ErrorBlock message={error} onRetry={() => void loadInitial()} />
          ) : jobs.length === 0 ? (
            <EmptyBlock />
          ) : (
            <>
              <div className="space-y-4">
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>

              {next && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={() => void loadMore()}
                    disabled={loadingMore}
                    className="btn-secondary"
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobsList;
